// @ts-strict-ignore
import { useSelector } from 'react-redux';
import {
    getIsLoading,
    isPhasedReleaseComplete,
    getIsApplePlatform,
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsAmazonAppStore,
    isGooglePlatform,
    isGoogleBetaIntegration,
    getIsHuaweiAppGallery,
    getIsReleaseAutomationInactiveBecauseOfASCSetting,
    isPhasedReleasePaused,
    getReleasePreviousVersionToAllUsersTooltip,
    getPausedPhasedReleaseWarning,
} from '../helpers';
import { RootState } from '../reducers/reducers';
import {
    AppleAppStoreData,
    AppleAppStoreDefaultSettings,
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreData,
    AppStoreDefaultSettings,
    AppStoreType,
    BetaBuild,
    BuildStatus,
    ChecklistItem,
    ChecklistItemStatusType,
    CIBuildInfo,
    GoogleAppStoreSettings,
    HuaweiAppGallerySettings,
    PhasedRelease,
    QAHistoryItem,
    RegressionStatus,
    ReleaseCandidateBuildInfo,
    ReleaseEntity,
    ReleaseStepStoreReview,
    ReleaseStepSubmission,
    ReleaseStepTakeoff,
    ReleaseType,
    BuildMatchingWarning,
} from '../types';
import * as React from 'react';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from './styles/globalStyles';
import {
    displayConsoleNameForAppStoreType,
    displayStoreNameForAppStoreType,
    displayStringForRolloutPercentage,
    displayTextForAppPlatform,
} from '../helpers/displayHelpers';
import { ReleaseUpdatable } from '../actions/updatable';
import Descriptions from 'antd/lib/descriptions';
import Button from 'antd/lib/button';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import List from 'antd/lib/list';
import Tooltip from 'antd/lib/tooltip';
import { iconForPlatform } from '../helpers/platformComponentHelpers';
import Alert from 'antd/lib/alert';
import { displayNameForIntegrationType } from '../helpers/integrationHelpers';
import { Nullable, ReleaseStepComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import {
    selectRelease,
    selectReleaseIsRunwayAutomated,
    selectAppStoreDefaults,
    selectAppStoreType,
    selectAutoReleaseStepAutomationRun,
    selectReleaseStep,
    selectShouldWarnAboutNewerCommits,
} from '../selectors/selectors';
import AppleAppStoreReleaseSettingsDescriptions from './appSettings/AppleAppStoreReleaseSettingsDescriptions';
import GooglePlayStoreReleaseSettingsDescriptions from './appSettings/GooglePlayStoreReleaseSettingsDescriptions';
import PropTypes from 'prop-types';
import { AppStoreItemsCard } from '@/components/AppStoreItems/AppStoreItemsCard';
import AppStoreBuilds from '@/components/AppStoreBuilds';
import Checkbox from 'antd/lib/checkbox';
import { renderReviewsSubmissionItemCard } from '@/components/StepContentSubmission/helpers';
import HuaweiAppGalleryReleaseSettingsDescriptions from '@/components/appSettings/HuaweiAppGalleryReleaseSettingsDescriptions';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import Flex from 'antd/lib/flex';

function getTitleAndSubtitleForPlatform(
    platform: AppPlatform,
    appStoreType: AppStoreType,
    displayableVersion: string,
    buildString: string,
    selectedBuildNumberString: string,
    storeName: string,
    appleAppStoreData: AppleAppStoreData,
) {
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isAmazonAppStore = getIsAmazonAppStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);

    let title = `Submit ${displayTextForAppPlatform(platform, appStoreType)} version ${displayableVersion}${
        isHuaweiAppGallery ? ' ' : ` (${buildString} ${selectedBuildNumberString}) `
    }for review?`;

    let subtitle = `Verify your current ${storeName} release settings for this update`;

    const itemsCount = appleAppStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.length;
    if (isAppleAppStore && itemsCount > 1) {
        title = `Submit ${itemsCount} items for review?`;
        subtitle = `Verify each of the items below. All items will be included in your ${displayableVersion} submission.`;
    }

    if (isAmazonAppStore) {
        title = `Submit ${displayTextForAppPlatform(platform, appStoreType)} version ${displayableVersion} for review?`;
        subtitle = `You are submitting the upcoming version containing ${buildString} ${selectedBuildNumberString}. This
                version will be released to 100% once it's been reviewed by Amazon.`;
    }

    return { title, subtitle };
}

const getSubmitButtonTextForPlatform = (selectedBuildNumberString: string, reviewSubmissionItemsCount: number) => {
    if (reviewSubmissionItemsCount > 1) {
        return `Submit ${reviewSubmissionItemsCount} items for review`;
    }

    return `Submit ${selectedBuildNumberString} for review`;
};

const AppleReleaseDefaults = ({ exportComplianceInfoUsesEncryption }: AppleAppStoreDefaultSettings) => {
    // const resetRatingText = resetSummaryRating ? 'Reset rating' : 'Keep existing rating';
    const encryptionText =
        exportComplianceInfoUsesEncryption ? 'Yes, my app uses encryption' : (
            "No, my app doesn't use non-exempt encryption"
        );

    const usesNonExemptEncryption = exportComplianceInfoUsesEncryption ? 'Yes' : null;

    const listItemStyle: React.CSSProperties = {
        color: antGrey.grey8,
        paddingTop: 4,
        paddingBottom: 4,
    };

    return (
        <Descriptions
            column={1}
            labelStyle={{ fontWeight: 600, color: antGrey.grey8 }}
            contentStyle={{ color: antGrey.grey8 }}
        >
            {/* <Descriptions.Item label="Reset summary rating">{resetRatingText}</Descriptions.Item> */}
            <Descriptions.Item label="Export compliance information">{encryptionText}</Descriptions.Item>
            {exportComplianceInfoUsesEncryption && (
                <Descriptions.Item>
                    <List split={false} size="small" itemLayout="vertical">
                        <List.Item style={listItemStyle}>
                            •{' '}
                            <Text strong style={{ color: antGrey.grey8 }}>
                                Uses non-exempt encryption:
                            </Text>{' '}
                            {usesNonExemptEncryption}
                        </List.Item>
                    </List>
                </Descriptions.Item>
            )}
        </Descriptions>
    );
};

interface AppStoreSubmitConfirmationModalProps extends ReleaseStepComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    displayableVersion: string;
    open: boolean;
    lastCompletedRelease?: ReleaseEntity;
    onClose: () => void;
    onSubmit: (
        releasePreviousSubmissionToAllUsersFirst: boolean,
        releaseBetaTrackToFullPercentagePriorToSubmission: boolean,
        shouldMatchPreviousPhasedRelease: boolean,
    ) => void;
    onShowReleaseSettingsModal: () => void;
    onShowReleaseDefaultsModal: () => void;
    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedBetaBuild: Nullable<BetaBuild>;
    autoStagedRolloutAutomationId: Nullable<string>;
    submissionCLIs: ChecklistItem[];
    appStoreReleaseSettings?: AppStoreDefaultSettings;
    appStoreData?: AppStoreData;
    regressionBuildInfo?: CIBuildInfo;
    regressionStatus?: RegressionStatus;
    latestReleasePhasedRelease: Nullable<PhasedRelease>;
    latestReleaseVersion: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    buildMatchingWarnings?: BuildMatchingWarning[];
}

const AppStoreSubmitConfirmationModal: React.FC<AppStoreSubmitConfirmationModalProps> = (
    props: AppStoreSubmitConfirmationModalProps,
) => {
    const [shouldShowSubmitOverlay, setShouldShowSubmitOverlay] = React.useState(false);
    const [shouldReleaseToAllUsersFirst, setShouldReleaseToAllUsersFirst] = React.useState(false);

    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId));
    const isReleaseRollback = release.type === ReleaseType.Rollback;
    const isReleaseHotfix = release.type === ReleaseType.Hotfix;
    const shouldWarnAboutNewerCommits = useSelector((state: RootState) =>
        selectShouldWarnAboutNewerCommits(state, props.releaseId),
    );

    const incompleteSubmissionStepCLIs = React.useMemo(() => {
        return props.submissionCLIs.reduce((count, checklistItem) => {
            if (
                !checklistItem.status ||
                (checklistItem.status &&
                    ![ChecklistItemStatusType.Approved, ChecklistItemStatusType.Passed].includes(
                        checklistItem.status.status,
                    ))
            ) {
                return count + 1;
            }

            return count;
        }, 0);
    }, [props.submissionCLIs]);

    const { appStoreData, appStoreReleaseSettings } = props;

    const isRunwayReleaseAutomationInactiveBecauseOfApple = useSelector((state: RootState) => {
        const appStoreType = selectAppStoreType(state, props.appId);
        const releaseAutomation = selectAutoReleaseStepAutomationRun(state, props.releaseId);
        const releaseStep = selectReleaseStep(state, props.releaseId, props.releaseStepType) as
            | ReleaseStepTakeoff
            | ReleaseStepSubmission
            | ReleaseStepStoreReview;

        const appStoreData = releaseStep?.data?.appStoreData as AppleAppStoreData;

        return getIsReleaseAutomationInactiveBecauseOfASCSetting(
            releaseAutomation,
            appStoreData,
            appStoreType,
            release.type,
        );
    });

    const willBeReleasedByRunway = useSelector((state: RootState) =>
        selectReleaseIsRunwayAutomated(state, props.appId, props.releaseId),
    );

    const appStoreDefaults = useSelector((state: RootState) => selectAppStoreDefaults(state, props.appId));

    const selectedAppStoreBuilds = props.appStoreSelectedBuilds;

    const selectedBetaBuild = props.selectedBetaBuild;

    const BoldText = ({ text }) => <strong>{text}</strong>;
    BoldText.propTypes = {
        text: PropTypes.string.isRequired,
    };
    const betaTrackName =
        selectedBetaBuild?.testTrack ? <BoldText text={selectedBetaBuild?.testTrack} /> : 'default beta';

    const shouldShowReleaseToFullPercentageCheckboxOption =
        selectedBetaBuild != null &&
        isGooglePlatform(props.platform) &&
        isGoogleBetaIntegration(selectedBetaBuild.appStoreBuild.appStoreBuild.integrationId);

    const [
        shouldReleaseBetaTrackToFullPercentagePriorToSubmission,
        setShouldReleaseBetaTrackToFullPercentagePriorToSubmission,
    ] = React.useState(false);

    const [shouldMatchPreviousPhasedRelease, setShouldMatchPreviousPhasedRelease] = React.useState(
        release?.shouldMatchPreviousPhasedRelease ?? false,
    );

    const handleBetaTrackCheckboxChange = (event) => {
        setShouldReleaseBetaTrackToFullPercentagePriorToSubmission(event.target.checked);
    };

    const handleReleasePreviousVersionToAllCheckboxChange = (event) => {
        setShouldReleaseToAllUsersFirst(event.target.checked);
    };

    const handleMatchPreviousRolloutPercentageCheckboxChange = (event) => {
        setShouldMatchPreviousPhasedRelease(event.target.checked);
    };

    const isApplePlatform = getIsApplePlatform(props.platform);
    const isAppleAppStore = getIsAppleAppStore(props.appStoreType);
    const isGooglePlayStore = getIsGooglePlayStore(props.appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(props.appStoreType);

    const appleAppStoreData = appStoreData as AppleAppStoreData;
    const appleAppStoreDefaults = appStoreDefaults as AppleAppStoreDefaultSettings;
    const googleAppStoreDefaults = appStoreDefaults as GoogleAppStoreSettings;

    const selectedBuildNumbers =
        selectedAppStoreBuilds && selectedAppStoreBuilds.map((build) => `#${build.appStoreBuild.buildNumber}`);
    const selectedBuildNumberString = selectedBuildNumbers?.join(', ');
    const buildString = selectedBuildNumbers?.length > 1 ? 'Builds' : 'Build';

    // Warning banner conditions

    let showSelectedBuildWarningBanner = false;
    let selectedBuildWarningMessage = null;

    const failedCIBuildSelectedBuild = selectedAppStoreBuilds?.find(
        (build) => build.associatedCIBuild?.buildStatus === BuildStatus.Failure,
    );

    // selected build has a failed CI build

    if (failedCIBuildSelectedBuild) {
        const ciBuild = failedCIBuildSelectedBuild.associatedCIBuild;
        const appStoreBuild = failedCIBuildSelectedBuild.appStoreBuild;

        showSelectedBuildWarningBanner = ciBuild.buildStatus === BuildStatus.Failure;
        selectedBuildWarningMessage =
            showSelectedBuildWarningBanner ?
                `The ${displayNameForIntegrationType(ciBuild.integrationId)} build (#${
                    ciBuild.buildIdentifier
                }) associated with the selected ${displayNameForIntegrationType(appStoreBuild.integrationId)} build (#${
                    appStoreBuild.buildNumber
                }) failed. Make sure this is actually the build you want to submit.`
            :   null;
    }

    const submitIsLoading = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.SubmitAppForReview, state, props.releaseId),
    );

    const lastCompletedVersion = props.latestReleaseVersion ?? props.lastCompletedRelease?.displayableVersion;
    const latestReleasePhasedRelease = props.latestReleasePhasedRelease ?? props.lastCompletedRelease?.phasedRelease;
    const phasedReleasePercent = latestReleasePhasedRelease?.phasedReleasePercent;

    const isLastCompletedReleaseFullyReleased =
        latestReleasePhasedRelease?.state ? isPhasedReleaseComplete(latestReleasePhasedRelease) : true;

    const shouldShowSubmitAndReleaseToAllOption =
        lastCompletedVersion &&
        !isLastCompletedReleaseFullyReleased &&
        !isApplePlatform &&
        !isReleaseRollback &&
        !isReleaseHotfix;

    const shouldShowShouldMatchPreviousPhasedReleaseOption =
        isReleaseHotfix && lastCompletedVersion && !isLastCompletedReleaseFullyReleased && isGooglePlayStore;

    const releasePreviousVersionToAllUsersTooltip =
        phasedReleasePercent ? getReleasePreviousVersionToAllUsersTooltip(phasedReleasePercent) : '';

    const isLastCompletedReleasePaused =
        latestReleasePhasedRelease?.state ? isPhasedReleasePaused(latestReleasePhasedRelease.state) : false;

    const pausedPhasedReleaseWarning =
        isLastCompletedReleasePaused && phasedReleasePercent ?
            getPausedPhasedReleaseWarning(lastCompletedVersion, phasedReleasePercent)
        :   null;

    const storeName = displayStoreNameForAppStoreType(props.appStoreType);
    const consoleName = displayConsoleNameForAppStoreType(props.appStoreType);
    const noDefaultsSet = !appleAppStoreDefaults && !googleAppStoreDefaults;
    const buttonIcon = iconForPlatform(props.platform);

    const { title, subtitle } = getTitleAndSubtitleForPlatform(
        props.platform,
        props.appStoreType,
        props.displayableVersion,
        buildString,
        selectedBuildNumberString,
        storeName,
        appleAppStoreData,
    );

    const showIncompleteChecklistItemsWarning = incompleteSubmissionStepCLIs > 0;

    const submitButtonText = React.useMemo(() => {
        const mainText = getSubmitButtonTextForPlatform(
            selectedBuildNumberString,
            appleAppStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.length,
        );

        if (!shouldReleaseToAllUsersFirst) {
            return mainText;
        }

        return `Accelerate ${lastCompletedVersion} to 100% and ${mainText.toLocaleLowerCase()}`;
    }, [
        selectedBuildNumberString,
        appleAppStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.length,
        shouldReleaseToAllUsersFirst,
        lastCompletedVersion,
    ]);

    // High priority warnings are shown on the top
    // Other warnings such as the phased release warning are shown in the body of the modal
    const hasHighPriorityWarning =
        showSelectedBuildWarningBanner || showIncompleteChecklistItemsWarning || shouldWarnAboutNewerCommits;

    const buildInfoHeader = (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 8,
                padding: '16px 20px',
            }}
        >
            <Text style={{ fontWeight: 600, color: antGrey.grey8 }}>
                App update for {storeName} version {props.displayableVersion}
            </Text>
            <AppStoreBuilds
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId}
                platform={props.platform}
                appStoreType={props.appStoreType}
                isSubmitted={false}
                appStoreSelectedBuilds={props.appStoreSelectedBuilds}
                additionalAppStoreSelectedBuilds={props.additionalAppStoreSelectedBuilds}
                buildMatchingWarnings={props.buildMatchingWarnings}
            />
        </div>
    );

    const shouldShowVersionSettingsOverview = isAppleAppStore || isGooglePlayStore;

    return (
        <Modal
            destroyOnClose
            title={title}
            onOk={() => {
                setShouldShowSubmitOverlay(true);
                props.onClose();
            }}
            onCancel={props.onClose}
            okButtonProps={{ loading: submitIsLoading, disabled: noDefaultsSet, icon: buttonIcon }}
            footer={(_, { CancelBtn }) => (
                <Space>
                    {<CancelBtn />}
                    <Button
                        onClick={() => {
                            setShouldShowSubmitOverlay(true);
                            props.onClose();
                        }}
                        type="primary"
                    >
                        {submitButtonText}
                    </Button>
                </Space>
            )}
            open={props.open}
            bodyStyle={{ backgroundColor: antGrey.grey3 }}
            centered
            width={700}
            afterClose={() => {
                if (shouldShowSubmitOverlay) {
                    props.onSubmit(
                        shouldReleaseToAllUsersFirst,
                        shouldReleaseBetaTrackToFullPercentagePriorToSubmission,
                        shouldMatchPreviousPhasedRelease,
                    );
                }

                setShouldReleaseToAllUsersFirst(false);
                setShouldShowSubmitOverlay(false);
            }}
        >
            {hasHighPriorityWarning && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 8,
                        marginBottom: 16,
                    }}
                >
                    {showSelectedBuildWarningBanner && selectedBuildWarningMessage && (
                        <Alert
                            showIcon
                            closable={false}
                            message={<Text>{selectedBuildWarningMessage}</Text>}
                            type="warning"
                        />
                    )}
                    {showIncompleteChecklistItemsWarning && (
                        <Alert
                            closable={false}
                            showIcon
                            message={`${incompleteSubmissionStepCLIs} checklist item${
                                incompleteSubmissionStepCLIs > 1 ? 's are' : ' is'
                            } incomplete for this step`}
                            type="warning"
                        />
                    )}
                    {shouldWarnAboutNewerCommits && (
                        <Alert
                            closable={false}
                            showIcon
                            message="There are commits on your release branch that are newer than your currently selected release candidate build."
                            type="warning"
                        />
                    )}
                </div>
            )}
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                }}
            >
                <Text
                    style={{
                        fontSize: 14,
                        color: antGrey.grey8,
                    }}
                >
                    {subtitle}
                </Text>
                {shouldShowVersionSettingsOverview && (
                    <AppStoreSubmitVersionSettingsOverview
                        header={buildInfoHeader}
                        items={[
                            {
                                title: `${storeName} release settings`,
                                onEdit: props.onShowReleaseSettingsModal,
                                content:
                                    appStoreReleaseSettings ?
                                        isAppleAppStore ?
                                            <AppleAppStoreReleaseSettingsDescriptions
                                                orgId={props.orgId}
                                                appId={props.appId}
                                                releaseId={props.releaseId}
                                                willBeReleasedByRunway={willBeReleasedByRunway}
                                                isRunwayReleaseAutomationInactiveBecauseOfApple={
                                                    isRunwayReleaseAutomationInactiveBecauseOfApple
                                                }
                                                appDefaultSettings={appStoreDefaults as AppleAppStoreDefaultSettings}
                                                appStoreReleaseSettings={
                                                    appStoreReleaseSettings as AppleAppStoreDefaultSettings
                                                }
                                                appStoreData={appleAppStoreData}
                                                isRollback={isReleaseRollback}
                                                labelStyle={{ fontWeight: 600, color: antGrey.grey8 }}
                                                contentStyle={{ color: antGrey.grey8 }}
                                                colon={true}
                                                stepRunComponentProps={null}
                                            />
                                        : isGooglePlayStore ?
                                            <Space
                                                direction="vertical"
                                                size={18}
                                                style={{
                                                    paddingBottom: 16,
                                                }}
                                            >
                                                <GooglePlayStoreReleaseSettingsDescriptions
                                                    orgId={props.orgId}
                                                    appId={props.appId}
                                                    autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
                                                    releaseId={props.releaseId}
                                                    isRollback={isReleaseRollback}
                                                    isSubmitAutomationDisabledMissingDefaults={false}
                                                    appDefaultSettings={appStoreDefaults as GoogleAppStoreSettings}
                                                    releaseSettings={appStoreReleaseSettings as GoogleAppStoreSettings}
                                                    labelStyle={{ fontWeight: 600, color: antGrey.grey8 }}
                                                    contentStyle={{ color: antGrey.grey8 }}
                                                    stepRunComponentProps={null}
                                                />
                                            </Space>
                                        : isHuaweiAppGallery ?
                                            <Space
                                                direction="vertical"
                                                size={18}
                                                style={{
                                                    paddingBottom: 16,
                                                }}
                                            >
                                                <HuaweiAppGalleryReleaseSettingsDescriptions
                                                    orgId={props.orgId}
                                                    appId={props.appId}
                                                    autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
                                                    isSubmitAutomationDisabledMissingDefaults={false}
                                                    appDefaultSettings={appStoreDefaults as HuaweiAppGallerySettings}
                                                    releaseSettings={
                                                        appStoreReleaseSettings as HuaweiAppGallerySettings
                                                    }
                                                    labelStyle={{ fontWeight: 600, color: antGrey.grey8 }}
                                                    contentStyle={{ color: antGrey.grey8 }}
                                                />
                                            </Space>
                                        :   null
                                    :   <Text type="warning">
                                            No release settings have been set. Unable to submit for review.
                                        </Text>,
                            },
                            ...(isAppleAppStore ?
                                [
                                    {
                                        title: 'Release defaults',
                                        onEdit: props.onShowReleaseDefaultsModal,
                                        content: (
                                            <Space direction="vertical" size={18}>
                                                <Text style={{ color: antGrey.grey8 }}>
                                                    Additionally, these defaults will be used for your submission{' '}
                                                    <Text strong style={{ color: antGrey.grey8 }}>
                                                        if answers are not already set on {consoleName}.
                                                    </Text>{' '}
                                                    <Tooltip title="Runway will NOT overwrite settings already found in App Store Connect for 'Reset iOS summary rating' or 'Export Compliance Information' questions.">
                                                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                                                    </Tooltip>
                                                </Text>
                                                {noDefaultsSet && (
                                                    <Text
                                                        style={{
                                                            color: runwayColors.statusYellow,
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        No release defaults set. Unable to submit for review.
                                                    </Text>
                                                )}
                                                <AppleReleaseDefaults {...appleAppStoreDefaults} />
                                            </Space>
                                        ),
                                    },
                                ]
                            :   []),
                        ]}
                    />
                )}
                {appleAppStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.map((reviewSubmissionItem) =>
                    renderReviewsSubmissionItemCard({
                        reviewSubmissionItemId: reviewSubmissionItem.id,
                        item: reviewSubmissionItem.item,
                        type: reviewSubmissionItem.type,
                        state: reviewSubmissionItem.state,
                        shouldHideASCLink: true,
                        stepRunComponentProps: props.stepRunComponentProps,
                    }),
                )}
                {shouldShowReleaseToFullPercentageCheckboxOption && (
                    <Checkbox onChange={handleBetaTrackCheckboxChange}>
                        Release to 100% on the {betaTrackName} track prior to submission
                    </Checkbox>
                )}
                {shouldShowSubmitAndReleaseToAllOption && (
                    <Flex vertical gap={16}>
                        <Flex align="center">
                            <Checkbox onChange={handleReleasePreviousVersionToAllCheckboxChange}>
                                Accelerate <b>{lastCompletedVersion}</b> to 100% of users before releasing new version
                            </Checkbox>
                            <TooltipInfoIcon tooltipContent={releasePreviousVersionToAllUsersTooltip} />
                        </Flex>

                        {shouldReleaseToAllUsersFirst && pausedPhasedReleaseWarning && (
                            <Alert showIcon type="warning" closable={false} message={pausedPhasedReleaseWarning} />
                        )}
                    </Flex>
                )}
                {shouldShowShouldMatchPreviousPhasedReleaseOption && (
                    <Flex align="center">
                        <Checkbox
                            defaultChecked={shouldMatchPreviousPhasedRelease}
                            onChange={handleMatchPreviousRolloutPercentageCheckboxChange}
                        >
                            Start this staged rollout percentage where previous release left off (currently{' '}
                            {displayStringForRolloutPercentage(phasedReleasePercent)})
                        </Checkbox>
                        <TooltipInfoIcon tooltipContent="Runway will start the hotfix staged rollout at the same percentage where the previous release's rollout left off (determined at time of hotfix release), then will increment from there according to the remainder of your configured staged rollout sequence." />
                    </Flex>
                )}
            </div>
        </Modal>
    );
};

interface VersionSettingsItem {
    title: React.ReactNode;
    onEdit: () => void;
    content: React.ReactNode;
}

interface AppStoreSubmitVersionSettingsOverviewProps {
    header: React.ReactNode;
    items: VersionSettingsItem[];
}

const AppStoreSubmitVersionSettingsOverview: React.FC<AppStoreSubmitVersionSettingsOverviewProps> = ({
    header,
    items,
}) => {
    return (
        <AppStoreItemsCard>
            {header}
            {items.map((item, index) => (
                <div
                    key={index}
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 20px',
                            alignItems: 'center',
                            borderBottom: `1px solid ${antGrey.grey5}`,
                            borderTop: `1px solid ${antGrey.grey5}`,
                        }}
                    >
                        <Text style={{ color: antGrey.grey8 }}>{item.title}</Text>
                        <Button
                            type="link"
                            onClick={item.onEdit}
                            icon={
                                <EditOutlined
                                    style={{
                                        color: runwayColors.primary,
                                    }}
                                />
                            }
                        >
                            Edit
                        </Button>
                    </div>
                    <div
                        style={{
                            padding: '16px 20px',
                            paddingBottom: 0,
                            backgroundColor: runwayColors.white,
                            overflow: 'hidden',
                        }}
                    >
                        {item.content}
                    </div>
                </div>
            ))}
        </AppStoreItemsCard>
    );
};

export default AppStoreSubmitConfirmationModal;
